import { VehicleExpenseResponse } from "../apiService";
import { ExpenseEntry, ExpenseType } from "../components/types";

// Convert API response to form data for editing
export const mapApiResponseToExpenseEntry = (
  expense: VehicleExpenseResponse
): ExpenseEntry => {
  // Map expense type from API format to UI format
  const expenseTypeMap: Record<ExpenseType, string> = {
    FUEL: "Fuel",
    // MAINTENANCE: "Maintenance",
    REPAIR: "Maintenance / Repair",
    DRIVER_CLEANER: "Driver/Cleaner",
    PARKING: "Parking",
    CHALLAN: "Challan",
    OTHER: "Other",
    OUTSOURCED_OWNER: "Outsourced Owner",
    PICKUP_MAN: "Pickup Man",
    MINOR_REPAIR: "Minor Repair",
  };

  const baseEntry: ExpenseEntry = {
    type: expenseTypeMap[expense.expenseType as ExpenseType] || "Other",
    expenseDate: expense.expenseDate,
    amount: expense.amount.toString(),
    paidTo: expense.paidTo,
    location: expense.location,
    contactNumber: expense.contactNumber || "",
    notes: expense.notes || "",

    // Initialize all fields with empty values
    fuelType: "",
    liters: "",
    pricePerLitre: "",
    bulkDiscountApplied: "",
    petrolPumpId: "",
    odometerReading: "",
    serviceType: "",
    serviceProvider: "",
    serviceProviderType: undefined,
    serviceProviderId: "",
    maintenanceItem: "",
    customMaintenanceItem: "",
    actionType: undefined,
    warrantyPeriod: "",
    warrantyStartDate: "",
    warrantyEndDate: "",
    customWarrantyPeriod: "",
    nextServiceDue: "",
    invoiceNumber: "",
    workOrderNumber: "",
    category: undefined,
    referenceId: "",
    customCategory: "",

    // Challan expense fields
    responsibleParty: "DRIVER",
    responsiblePartyName: "",
    responsiblePartyContact: "",
    challanType: "",
    challanNumber: "",
    challanAction: "",
  };

  // Populate type-specific fields from details
  if (expense.details) {
    const details = expense.details;

    switch (expense.expenseType) {
      case "FUEL":
        baseEntry.fuelType = details.fuelType || "";
        baseEntry.liters = details.liters?.toString() || "";
        baseEntry.pricePerLitre = details.pricePerLitre?.toString() || "";
        baseEntry.bulkDiscountApplied =
          details.bulkDiscountApplied?.toString() || "";
        baseEntry.petrolPumpId = details.petrolPumpId || "";
        baseEntry.odometerReading = details.odometerReading?.toString() || "";
        break;

      case "MAINTENANCE":
      case "REPAIR":
        baseEntry.serviceType = details.serviceType || "";
        baseEntry.serviceProviderType = details.serviceProviderType;
        baseEntry.serviceProviderId = details.serviceProviderId || "";
        baseEntry.maintenanceItem = details.maintenanceItem || "";
        baseEntry.customMaintenanceItem = details.customMaintenanceItem || "";
        baseEntry.actionType = details.actionType;
        baseEntry.warrantyPeriod = details.warrantyPeriod || "";
        baseEntry.warrantyStartDate = details.warrantyStartDate || "";
        baseEntry.warrantyEndDate = details.warrantyEndDate || "";
        baseEntry.customWarrantyPeriod = details.customWarrantyPeriod || "";
        baseEntry.nextServiceDue = details.nextServiceDue?.toString() || "";
        baseEntry.invoiceNumber = details.invoiceNumber || "";
        baseEntry.workOrderNumber = details.workOrderNumber || "";
        baseEntry.odometerReading = details.odometerReading?.toString() || "";
        break;

      case "DRIVER_CLEANER":
      case "PARKING":
      case "OTHER":
      case "INSURANCE":
        baseEntry.category = details.category;
        baseEntry.referenceId = details.referenceId || "";
        baseEntry.customCategory = details.customCategory || "";
        break;

      case "CHALLAN":
        baseEntry.responsibleParty = details.responsibleParty || "";
        baseEntry.responsiblePartyName = details.responsiblePartyName || "";
        baseEntry.responsiblePartyContact = details.responsiblePartyContact || "";
        baseEntry.challanType = details.challanType || "";
        baseEntry.challanNumber = details.challanNumber || "";
        baseEntry.challanAction = details.challanAction || "";
    }
  }

  return baseEntry;
};

// Format expense type for display
export const formatExpenseType = (type: string): string => {
  switch (type) {
    case "DRIVER_CLEANER":
      return "Driver/Cleaner";
    case "FUEL":
      return "Fuel";
    case "MAINTENANCE":
      return "Maintenance";
    case "REPAIR":
      return "Maintenance/Repair";
    case "PARKING":
      return "Parking";
    case "OTHER":
      return "Other";
    case "INSURANCE":
      return "Insurance";
    case "OUTSOURCED_OWNER":
      return "Outsourced Owner";
    case "PICKUP_MAN":
      return "Pickup Man";
    default:
      return type.charAt(0) + type.slice(1).toLowerCase();
  }
};

// Get color for expense type chip
export const getExpenseTypeColor = (
  type: string
): "success" | "warning" | "info" | "secondary" | "error" | "primary" => {
  switch (type) {
    case "FUEL":
      return "success";
    case "MAINTENANCE":
    case "REPAIR":
      return "warning";
    case "DRIVER_CLEANER":
      return "info";
    case "PARKING":
      return "secondary";
    case "INSURANCE":
      return "error";
    default:
      return "primary";
  }
};

// Calculate total expenses for a vehicle
export const calculateVehicleTotal = (
  expenses: VehicleExpenseResponse[]
): number => {
  // Safety check to ensure expenses is an array
  if (!expenses || !Array.isArray(expenses)) {
    return 0;
  }

  return expenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount.toString()),
    0
  );
};

// Format currency for display
export const formatCurrency = (amount?: number | string): string => {
  if (!amount) {
    return "₹0";
  }
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return `₹${numAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

// Format date for display
export const formatDate = (
  dateString: string,
  format: "short" | "long" = "short"
): string => {
  const date = new Date(dateString);

  if (format === "short") {
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

// Truncate text with ellipsis
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

// Group expenses by vehicle number
export const groupExpensesByVehicle = (
  expenses: VehicleExpenseResponse[]
): Record<string, VehicleExpenseResponse[]> => {
  // Safety check to ensure expenses is an array
  if (!expenses || !Array.isArray(expenses)) {
    return {};
  }

  return expenses.reduce((acc, expense) => {
    if (!acc[expense.vehicleNumber]) {
      acc[expense.vehicleNumber] = [];
    }
    acc[expense.vehicleNumber].push(expense);
    return acc;
  }, {} as Record<string, VehicleExpenseResponse[]>);
};

// Group expenses by type
export const groupExpensesByType = (
  expenses: VehicleExpenseResponse[]
): Record<string, VehicleExpenseResponse[]> => {
  // Safety check to ensure expenses is an array
  if (!expenses || !Array.isArray(expenses)) {
    return {};
  }

  return expenses.reduce((acc, expense) => {
    if (!acc[expense.expenseType]) {
      acc[expense.expenseType] = [];
    }
    acc[expense.expenseType].push(expense);
    return acc;
  }, {} as Record<string, VehicleExpenseResponse[]>);
};

// Calculate expense statistics
export interface ExpenseStats {
  totalAmount: number;
  totalCount: number;
  averageAmount: number;
  byType: Record<string, { count: number; total: number; average: number }>;
}

export const calculateExpenseStats = (
  expenses: VehicleExpenseResponse[]
): ExpenseStats => {
  // Safety check to ensure expenses is an array
  if (!expenses || !Array.isArray(expenses)) {
    return {
      totalAmount: 0,
      totalCount: 0,
      averageAmount: 0,
      byType: {},
    };
  }

  const totalAmount = expenses.reduce(
    (sum, expense) => sum + parseFloat(expense.amount.toString()),
    0
  );
  const totalCount = expenses.length;
  const averageAmount = totalCount > 0 ? totalAmount / totalCount : 0;

  const byType = expenses.reduce((acc, expense) => {
    const type = expense.expenseType;
    if (!acc[type]) {
      acc[type] = { count: 0, total: 0, average: 0 };
    }
    acc[type].count += 1;
    acc[type].total += parseFloat(expense.amount.toString());
    acc[type].average = acc[type].total / acc[type].count;
    return acc;
  }, {} as Record<string, { count: number; total: number; average: number }>);

  return {
    totalAmount,
    totalCount,
    averageAmount,
    byType,
  };
};

// Validate expense data
export const validateExpenseData = (expense: ExpenseEntry): string[] => {
  const errors: string[] = [];

  if (!expense.amount || parseFloat(expense.amount) <= 0) {
    errors.push("Amount must be greater than 0");
  }

  if (!expense.paidTo.trim()) {
    errors.push("Paid To is required");
  }

  if (!expense.location.trim()) {
    errors.push("Location is required");
  }

  if (!expense.expenseDate) {
    errors.push("Expense date is required");
  }

  // Type-specific validations
  switch (expense.type) {
    case "Fuel":
      if (!expense.fuelType) {
        errors.push("Fuel type is required");
      }
      if (!expense.liters || parseFloat(expense.liters) <= 0) {
        errors.push("Liters must be greater than 0");
      }
      break;

    case "Maintenance":
    case "Repair":
      if (!expense.serviceType) {
        errors.push("Service type is required");
      }
      break;

    case "Driver/Cleaner":
    case "Parking":
    case "Other":
      if (!expense.category) {
        errors.push("Category is required");
      }
      break;
  }

  return errors;
};
